import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Search, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, newKey, qty, rp, todayJkt } from "@/lib/api";
import { EmptyState, Field, Loading, PageHeader, QtyInput, RupiahInput, SectionTitle, SimBadge, StatCard, StatusBadge } from "@/components/common";
import { History } from "@/components/History";

export const CONFIG = {
  supplier: { title: "Kasbon Pabrik", color: "#2357c9", party: "Nama Pabrik/Pemasok", partyPh: "Contoh: Pabrik Kedelai Makmur", save: "Simpan Kasbon Pabrik",
    pay: "Bayar Kasbon", list: "Daftar Kasbon Pabrik", sub: "Bahan atau barang yang sudah diterima tapi belum dibayar lunas.", total: "Sisa Kasbon Pabrik",
    paidNow: "Uang muka yang sudah dibayar (opsional)", kinds: "sup_credit,sup_payment", paidOk: "Pembayaran tercatat. Uang Kas berkurang dan masuk ke riwayat Pengeluaran." },
  customer: { title: "Kasbon Pelanggan", color: "#6d4fd8", party: "Nama Pelanggan", partyPh: "Contoh: Warung Bu Sari", save: "Simpan Kasbon Pelanggan",
    pay: "Terima Pembayaran", list: "Daftar Kasbon Pelanggan", sub: "Tahu yang sudah diambil pelanggan tapi belum dibayar lunas.", total: "Sisa Kasbon Pelanggan",
    paidNow: "Sudah dibayar sekarang (opsional)", kinds: "cust_credit,cust_payment", paidOk: "Pembayaran diterima. Uang Kas bertambah dan masuk ke riwayat Pemasukan." },
};

function CreditForm({ type, parties, onSaved }) {
  const { ws } = useAuth();
  const c = CONFIG[type];
  const empty = { party: "", date: todayJkt(), item: type === "supplier" ? "kedelai" : "Tahu Kuning", item_name: "", qty: "", unit: "Papan", amount: 0, paid_now: 0, note: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const mat = type === "supplier" ? ws.materials.find((m) => m.key === f.item) : null;
  const other = f.item === "__other";
  const save = async () => {
    if (!f.party.trim()) { toast.error(`Isi ${c.party.toLowerCase()}.`); return; }
    if (!f.amount) { toast.error("Isi nilai kasbon."); return; }
    if (f.paid_now >= f.amount) { toast.error("Jika sudah dibayar penuh, catat di Pemasukan/Pengeluaran, bukan kasbon."); return; }
    if (mat && !(Number(f.qty) > 0)) { toast.error("Isi jumlah bahan yang diterima."); return; }
    if (type === "supplier" && other && !f.item_name.trim()) { toast.error("Isi nama bahan/barang."); return; }
    const body = { party: f.party, date: f.date, amount: f.amount, paid_now: f.paid_now, note: f.note, qty: Number(f.qty) || 0, client_key: key.current };
    if (type === "supplier") Object.assign(body, mat ? { material: mat.key } : { item_name: f.item_name, unit: f.unit });
    else Object.assign(body, other ? { item_name: f.item_name, unit: f.unit } : { product: f.item, unit: f.unit });
    setBusy(true);
    try {
      await api.post(`/credits/${type}`, body);
      toast.success(mat ? `Kasbon tersimpan. Stok ${mat.name} bertambah ${f.qty} ${mat.unit}.` : "Kasbon tersimpan.");
      setF({ ...empty, date: f.date }); key.current = newKey(); onSaved();
    } catch (e) { toast.error(errMsg(e)); if (e?.response?.status === 409) key.current = newKey(); }
    finally { setBusy(false); }
  };
  const options = type === "supplier"
    ? [...ws.materials.map((m) => [m.key, m.name]), ["__other", "Barang lain (tidak masuk stok)"]]
    : [...ws.products.map((p) => [p, p]), ["__other", "Lainnya"]];
  return (
    <div className="jt-card p-5 sm:p-6 space-y-3.5" data-testid={`${type}-credit-form`}>
      <SectionTitle>Catat {c.title}</SectionTitle>
      <Field label={c.party}>
        <input className="jt-input" list={`${type}-parties`} placeholder={c.partyPh} value={f.party} onChange={(e) => setF({ ...f, party: e.target.value })} data-testid="credit-party-input" />
        <datalist id={`${type}-parties`}>{parties.map((p) => <option key={p} value={p} />)}</datalist>
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Tanggal"><input type="date" className="jt-input" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="credit-date-input" /></Field>
        <Field label={type === "supplier" ? "Nama Bahan/Barang" : "Produk"}>
          <select className="jt-input" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} data-testid="credit-item-select">
            {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>
      </div>
      {other && <Field label="Nama barang"><input className="jt-input" value={f.item_name} onChange={(e) => setF({ ...f, item_name: e.target.value })} data-testid="credit-item-name-input" /></Field>}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Jumlah"><QtyInput value={f.qty} onChange={(v) => setF({ ...f, qty: v })} testid="credit-qty-input" /></Field>
        <Field label="Satuan">
          {mat ? <input className="jt-input" value={mat.unit} disabled data-testid="credit-unit-display" /> : type === "customer" && !other
            ? <select className="jt-input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="credit-unit-select">{ws.product_units.map((u) => <option key={u}>{u}</option>)}</select>
            : <input className="jt-input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="credit-unit-input" />}
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={type === "supplier" ? "Nilai Transaksi" : "Nilai Kasbon"}><RupiahInput value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testid="credit-amount-input" /></Field>
        <Field label={c.paidNow}><RupiahInput value={f.paid_now} onChange={(v) => setF({ ...f, paid_now: v })} testid="credit-paid-now-input" /></Field>
      </div>
      <Field label="Keterangan"><input className="jt-input" placeholder="Contoh: bayar minggu depan" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="credit-note-input" /></Field>
      <div className="text-sm text-muted-foreground">Sisa kasbon: <b className="text-foreground" data-testid="credit-form-remaining">{rp(Math.max(0, f.amount - f.paid_now))}</b></div>
      <button className="jt-btn w-full text-white" style={{ background: c.color }} disabled={busy} onClick={save} data-testid="credit-save-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {c.save}</button>
    </div>
  );
}

function PayForm({ credits, type, onPaid }) {
  const c = CONFIG[type];
  const open = credits.filter((x) => x.remaining > 0);
  const [cid, setCid] = useState(open[0]?.id || "");
  const [amount, setAmount] = useState(0);
  const [date, setDate] = useState(todayJkt());
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  const sel = open.find((x) => x.id === cid);
  useEffect(() => { if (!sel && open[0]) setCid(open[0].id); }, [sel, open]);
  if (!open.length) return <p className="text-sm font-semibold text-[#1f6b39] bg-[#e3f3e5] rounded-xl px-4 py-3" data-testid="party-all-paid">Semua kasbon sudah lunas.</p>;
  const pay = async () => {
    if (!amount) { toast.error("Isi jumlah pembayaran."); return; }
    if (amount > sel.remaining) { toast.error(`Pembayaran melebihi sisa kasbon (${rp(sel.remaining)}).`); return; }
    setBusy(true);
    try { await api.post(`/credits/${cid}/pay`, { amount, date, note, client_key: key.current }); toast.success(c.paidOk); setAmount(0); setNote(""); key.current = newKey(); onPaid(); }
    catch (e) { toast.error(errMsg(e)); if (e?.response?.status === 409) key.current = newKey(); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border p-4 space-y-3 bg-white/70" data-testid="pay-form">
      <div className="font-extrabold text-[var(--jt-emerald)]">{c.pay}</div>
      <Field label="Bayar untuk kasbon">
        <select className="jt-input" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="pay-credit-select">
          {open.map((x) => <option key={x.id} value={x.id}>{fmtDate(x.date)} — {x.item_name || x.product || "Kasbon"} — sisa {rp(x.remaining)}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Jumlah dibayar"><RupiahInput value={amount} onChange={setAmount} testid="pay-amount-input" /></Field>
        <Field label="Tanggal"><input type="date" className="jt-input" value={date} min={sel?.date} onChange={(e) => setDate(e.target.value)} data-testid="pay-date-input" /></Field>
      </div>
      <div className="flex gap-2 flex-wrap">
        <button type="button" className="text-xs font-bold px-3 py-1.5 rounded-full bg-[#eef6ee] text-[#1f6b39]" onClick={() => setAmount(sel.remaining)} data-testid="pay-full-button">Bayar lunas {rp(sel?.remaining)}</button>
      </div>
      <Field label="Keterangan"><input className="jt-input" placeholder="Opsional" value={note} onChange={(e) => setNote(e.target.value)} data-testid="pay-note-input" /></Field>
      <button className="jt-btn w-full text-white" style={{ background: c.color }} disabled={busy} onClick={pay} data-testid="pay-submit-button">{busy && <Loader2 className="w-4 h-4 animate-spin" />} {c.pay}</button>
    </div>
  );
}

function PartyDialog({ party, credits, type, onClose, onPaid }) {
  return (
    <Dialog open={!!party} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-[var(--jt-ivory)]" data-testid="party-dialog">
        <DialogHeader><DialogTitle>{party}</DialogTitle><DialogDescription>Riwayat kasbon dan pembayaran.</DialogDescription></DialogHeader>
        <PayForm credits={credits} type={type} onPaid={onPaid} />
        <div className="space-y-3">
          {credits.map((x) => (
            <div key={x.id} className="rounded-2xl border p-4 bg-white/60" data-testid="party-credit-item">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-bold">{fmtDate(x.date)} · {x.item_name || x.product || "Kasbon"}{x.qty ? ` ${qty(x.qty)} ${x.unit || ""}` : ""}</span>
                {x.is_simulation && <SimBadge />}<span className="ml-auto"><StatusBadge status={x.credit_status} /></span>
              </div>
              {x.note && <div className="text-xs text-muted-foreground mt-0.5">{x.note}</div>}
              <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                <div><div className="text-xs text-muted-foreground">Nilai</div><b>{rp(x.amount)}</b></div>
                <div><div className="text-xs text-muted-foreground">Dibayar</div><b className="text-[#1f7a46]">{rp(x.paid)}</b></div>
                <div><div className="text-xs text-muted-foreground">Sisa</div><b className="text-[#c0392b]">{rp(x.remaining)}</b></div>
              </div>
              {x.payments.length > 0 && <ul className="mt-3 text-xs space-y-1 border-t pt-2">{x.payments.map((p) => <li key={p.id} className="flex justify-between"><span>{fmtDate(p.date)} · {p.note}</span><b>{rp(p.amount)}</b></li>)}</ul>}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CreditModule({ type, icon }) {
  const { bump, version } = useAuth();
  const c = CONFIG[type];
  const [credits, setCredits] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("open");
  const [party, setParty] = useState(null);
  const [reload, setReload] = useState(0);
  const load = useCallback(() => api.get(`/credits/${type}`).then(({ data }) => setCredits(data.credits)).catch((e) => toast.error(errMsg(e))), [type]);
  useEffect(() => { load(); }, [load, version, reload]);
  const changed = () => { setReload((x) => x + 1); bump(); };
  const groups = useMemo(() => {
    const m = {};
    (credits || []).forEach((x) => { const g = (m[x.party] ||= { party: x.party, amount: 0, paid: 0, remaining: 0, n: 0, sim: false, last: x.date }); g.amount += x.amount; g.paid += x.paid; g.remaining += x.remaining; g.n += 1; g.sim ||= x.is_simulation; });
    return Object.values(m).sort((a, b) => b.remaining - a.remaining);
  }, [credits]);
  const shown = groups.filter((g) => (filter === "all" || (filter === "open" ? g.remaining > 0 : g.remaining === 0)) && g.party.toLowerCase().includes(q.toLowerCase()));
  const total = groups.reduce((s, g) => s + g.remaining, 0);
  const status = (g) => (g.remaining === 0 ? "Lunas" : g.paid > 0 ? "Dibayar Sebagian" : "Belum Dibayar");
  return (
    <div data-testid={`${type}-credit-page`}>
      <PageHeader icon={icon} color={c.color} title={c.title} subtitle={c.sub} />
      <div className="grid xl:grid-cols-[1fr_1.1fr] gap-6">
        <CreditForm type={type} parties={groups.map((g) => g.party)} onSaved={changed} />
        <div className="space-y-6">
          <StatCard icon={icon} color={c.color} label={c.total} value={rp(total)} hint={`${groups.filter((g) => g.remaining > 0).length} ${type === "supplier" ? "pemasok" : "pelanggan"} belum lunas`} testid={`${type}-credit-total`} />
          <div className="jt-card p-5 sm:p-6">
            <SectionTitle>{c.list}</SectionTitle>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input className="jt-input pl-9" placeholder="Cari nama..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="credit-search-input" /></div>
              <div className="flex rounded-xl border bg-white p-1 text-sm font-semibold">
                {[["open", "Belum Lunas"], ["paid", "Lunas"], ["all", "Semua"]].map(([v, l]) => (
                  <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1.5 rounded-lg ${filter === v ? "bg-[var(--jt-emerald-2)] text-white" : ""}`} data-testid={`credit-filter-${v}`}>{l}</button>
                ))}
              </div>
            </div>
            {credits === null ? <Loading /> : shown.length === 0 ? <EmptyState text={q ? "Tidak ada nama yang cocok." : "Belum ada kasbon di daftar ini."} testid="credit-list-empty" /> : (
              <div className="divide-y divide-[var(--jt-gold)]/15">
                {shown.map((g) => (
                  <button key={g.party} onClick={() => setParty(g.party)} className="w-full flex items-center gap-3 py-3 text-left hover:bg-black/[0.02] rounded-lg px-1" data-testid="credit-party-row">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5"><span className="font-bold">{g.party}</span>{g.sim && <SimBadge />}</div>
                      <div className="text-xs text-muted-foreground">{g.n} kasbon · dibayar {rp(g.paid)} dari {rp(g.amount)}</div>
                    </div>
                    <div className="text-right"><div className="font-extrabold">{rp(g.remaining)}</div><StatusBadge status={status(g)} /></div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="jt-card p-5 sm:p-6">
            <SectionTitle>Riwayat Kasbon & Pembayaran</SectionTitle>
            <History kinds={c.kinds} reloadKey={reload} onChanged={changed} testid={`${type}-credit-history`} />
          </div>
        </div>
      </div>
      <PartyDialog party={party} type={type} credits={(credits || []).filter((x) => x.party === party)} onClose={() => setParty(null)} onPaid={changed} />
    </div>
  );
}
